import type { StatusIcon, StatusTone } from './statusContent';

const toneClasses: Record<StatusTone, string> = {
  success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  warning: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  info: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  danger: 'bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400',
  neutral: 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-400',
};

const iconPaths: Record<Exclude<StatusIcon, 'position'>, string> = {
  check: 'm4.5 12.75 6 6 9-13.5',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  'badge-check': 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  users:
    'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
  x: 'M6 18 18 6M6 6l12 12',
  'no-symbol':
    'M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636',
};

interface StatusIconBadgeProps {
  tone: StatusTone;
  icon: StatusIcon;
  /** Shown inside the circle when `icon` is `position`. */
  waitlistPosition?: number | null;
  size?: 'sm' | 'lg';
  /** Extra classes on the circle (e.g. `pop mx-auto` for the large headline layout). */
  className?: string;
}

/**
 * The tone-colored circle + status icon shared by the large `StatusMessage` headline
 * and the compact event-page registered card. Keeps the SVG paths in one place.
 */
export default function StatusIconBadge({
  tone,
  icon,
  waitlistPosition,
  size = 'lg',
  className = '',
}: StatusIconBadgeProps) {
  const circle = size === 'lg' ? 'h-16 w-16' : 'h-9 w-9';
  const glyph = size === 'lg' ? 'h-8 w-8' : 'h-5 w-5';
  const position = size === 'lg' ? 'text-2xl' : 'text-xs';

  return (
    <div className={`flex ${circle} shrink-0 items-center justify-center rounded-full ${toneClasses[tone]} ${className}`}>
      {icon === 'position' ? (
        <span className={`${position} font-extrabold`}>#{waitlistPosition}</span>
      ) : (
        <svg className={glyph} fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[icon]} />
        </svg>
      )}
    </div>
  );
}
